import axios from 'axios';

import { company } from '~/company/Company';
import { DOMAIN, REASON, reportWarning } from '~/services/telemetry';
import { mainStore, orderStore, userStore } from '~/stores';
import { isCookieConsentGranted } from '~/utils/cookies';

import { REQUEST_TIMEOUT } from './constants';

const axiosInstance = axios.create({
  baseURL: 'https://bi.tools.jiffygrocery.co.uk/',
  timeout: REQUEST_TIMEOUT,
});

interface BIRequestsInterface {
  sendAnalytics(
    eventName: string,
    data: Record<string, any> | Record<string, any>[],
  ): Promise<unknown>;
}

export const BIRequests: BIRequestsInterface = {
  sendAnalytics: (eventName, data) => {
    if (import.meta.env.DEV) {
      return Promise.resolve();
    }

    if (!mainStore.isRN && !isCookieConsentGranted()) {
      return Promise.resolve();
    }

    // Every BI event from the app carries the platform, and the collector drops
    // the ones where it is empty. `appPlatform` stays empty until the native
    // handshake lands, so a handshake that breaks does not degrade BI — it
    // silently deletes it, which is how a previous drop in CityDrinks numbers
    // read as customers leaving rather than as telemetry failing. Reported
    // once per session; the send still goes out, because deciding what the
    // collector accepts is not this layer's call.
    if (mainStore.isRN && !mainStore.appPlatform) {
      reportWarning(DOMAIN.analytics, 'BI event sent without a platform', {
        reason: REASON.analytics.biPlatformMissing,
        once: true,
        extras: { eventName },
      });
    }

    return axiosInstance.post(
      'bi/event/',
      {
        eventName,
        properties: data,
        os: {
          platform: mainStore.appPlatform,
          version: mainStore.appVersion,
        },
        userId: userStore.personalData.id,
        clientSource: 'App',
        deviceId: userStore.deviceId,
        whCode: orderStore.etaCalculation?.warehouse?.code || '',
      },
      {
        headers: {
          'api-key': import.meta.env.REACT_APP_BI_API_KEY,
          'x-company-id': company.config.id,
        },
      },
    );
  },
};
